import type { FeedbackSelection } from './inspect'
import { createAudioRecorder, type AudioCapture } from './audio'
import { submitFeedback, type IngestResult } from './ingest-client'

const SHEET_CSS = `
:host { all: initial; }
* { box-sizing: border-box; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif; }
.backdrop {
  position: fixed; inset: 0; background: rgba(17,16,15,.45); z-index: 2147483646;
}
.sheet {
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 2147483647;
  background: #faf8f5; color: #1c1917; border-radius: 16px 16px 0 0;
  padding: 10px 16px calc(14px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -8px 32px rgba(0,0,0,.18);
  max-height: min(86vh, 640px); overflow: auto;
}
@media (min-width: 768px) {
  .backdrop { background: rgba(17,16,15,.28); }
  .sheet {
    left: auto; right: 20px; bottom: 84px;
    width: min(400px, calc(100vw - 40px));
    border-radius: 16px;
    max-height: min(520px, 70vh);
    box-shadow: 0 16px 48px rgba(0,0,0,.28);
    padding-bottom: 14px;
  }
}
.grab {
  width: 36px; height: 4px; border-radius: 999px; background: #d6d3d1;
  margin: 0 auto 10px;
}
@media (min-width: 768px) { .grab { display: none; } }
.sheet-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 10px; margin-bottom: 10px;
}
.sheet-head h2 { font-size: 16px; margin: 0; line-height: 1.25; padding-top: 2px; flex: 1; font-weight: 600; }
.sheet-close {
  width: 32px; height: 32px; border: 0; border-radius: 999px; cursor: pointer;
  background: #e7e5e4; color: #44403c; font-size: 18px; line-height: 1;
  display: grid; place-items: center; flex-shrink: 0;
}
.target-card {
  display: grid; grid-template-columns: 52px 1fr; gap: 10px;
  background: #fff; border: 1px solid #e7e5e4; border-radius: 12px;
  padding: 10px; margin-bottom: 12px;
}
.target-thumb {
  width: 52px; height: 52px; border-radius: 8px;
  background: linear-gradient(145deg, rgba(109,40,217,.18), #fde68a55);
  border: 2px solid #6D28D9; display: grid; place-items: center;
  font-size: 10px; font-weight: 700; color: #6D28D9; text-align: center; line-height: 1.15;
}
.target-meta { font-size: 11px; color: #78716c; min-width: 0; }
.target-meta .tag {
  display: inline-block; background: rgba(109,40,217,.12); color: #5b21b6;
  border-radius: 999px; padding: 1px 7px; font-weight: 700; margin-bottom: 4px;
}
.target-meta .quote {
  color: #1c1917; font-size: 13px; font-weight: 600; line-height: 1.3;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.hint { font-size: 11px; color: #a8a29e; margin: 0 0 10px; }
.voice-chip {
  display: none; align-items: center; gap: 8px;
  background: #f5f3ff; border: 1px solid rgba(109,40,217,.35);
  border-radius: 999px; padding: 6px 10px 6px 12px;
  font-size: 12px; font-weight: 600; color: #5b21b6; margin-bottom: 10px;
}
.voice-chip.show { display: flex; }
.voice-chip .x {
  border: 0; background: transparent; color: #78716c; cursor: pointer;
  font-size: 14px; line-height: 1; padding: 2px 4px; margin-left: auto;
}
.composer {
  display: flex; align-items: flex-end; gap: 8px;
  background: #fff; border: 1px solid #e7e5e4; border-radius: 22px;
  padding: 6px 6px 6px 12px;
}
.composer.recording {
  border-color: #f87171;
  box-shadow: 0 0 0 2px rgba(248,113,113,.2);
}
.composer.ready {
  border-color: #86efac;
  box-shadow: 0 0 0 2px rgba(134,239,172,.25);
}
.composer textarea {
  flex: 1; border: 0; outline: none; resize: none; background: transparent;
  min-height: 40px; max-height: 160px; overflow-y: auto; padding: 8px 0;
  font: 14px/1.35 ui-sans-serif, system-ui, sans-serif; color: #1c1917;
}
.composer-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
.mic-split {
  display: flex; align-items: stretch; height: 40px;
  border-radius: 999px; overflow: hidden;
  border: 1.5px solid rgba(109,40,217,.35);
  background: rgba(109,40,217,.06);
}
.mic-split.recording {
  border-color: #f87171; background: rgba(239,68,68,.12);
}
.mic-main, .mic-chev {
  border: 0; background: transparent; cursor: pointer; color: #6D28D9;
  display: grid; place-items: center; height: 100%;
}
.mic-main { width: 34px; font-size: 16px; }
.mic-chev {
  width: 22px; border-left: 1px solid rgba(109,40,217,.2);
}
.mic-split.recording .mic-main,
.mic-split.recording .mic-chev { color: #ef4444; }
.mic-wrap { position: relative; }
.mic-wrap[hidden], .icon-btn.send[hidden] { display: none !important; }
.icon-btn.send {
  width: 40px; height: 40px; border-radius: 999px; border: 0; cursor: pointer;
  display: grid; place-items: center; background: #6D28D9; color: #fff; flex-shrink: 0;
}
.icon-btn.send:disabled {
  opacity: .35; cursor: not-allowed; background: #d6d3d1; color: #78716c;
}
.mic-menu {
  display: none; position: absolute; right: 0; bottom: calc(100% + 6px);
  background: #fff; border: 1px solid #e7e5e4; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.12); min-width: 180px; padding: 4px; z-index: 5;
}
.mic-menu.open { display: block; }
.mic-menu button {
  display: block; width: 100%; text-align: left; border: 0; background: transparent;
  padding: 8px 10px; border-radius: 8px; font: 600 12px/1.3 ui-sans-serif, system-ui, sans-serif;
  color: #1c1917; cursor: pointer;
}
.mic-menu button:hover { background: #f5f3ff; }
.mic-menu button.active { color: #6D28D9; }
.status { margin-top: 8px; font-size: 12px; color: #57534e; min-height: 1.2em; }
.error { color: #b91c1c; }
`

const MIC_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>`
const SEND_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`
const STOP_SVG = `<span style="width:10px;height:10px;border-radius:2px;background:#ef4444;display:block"></span>`

function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
}

export function openCaptureSheet(input: {
  apiBase: string
  token: string
  selection: FeedbackSelection
  onDone?: () => void
  onSent?: (result: IngestResult, preview: string) => void
}): void {
  const root = document.createElement('div')
  root.setAttribute('data-klfb-root', '1')
  const shadow = root.attachShadow({ mode: 'open' })
  document.body.appendChild(root)

  const style = document.createElement('style')
  style.textContent = SHEET_CSS
  shadow.appendChild(style)

  const backdrop = document.createElement('div')
  backdrop.className = 'backdrop'
  shadow.appendChild(backdrop)

  const sheet = document.createElement('div')
  sheet.className = 'sheet'
  sheet.setAttribute('role', 'dialog')
  sheet.setAttribute('aria-label', 'Feedback')
  const label = input.selection.feedbackId || input.selection.tag
  const quote = input.selection.textPreview || input.selection.selector
  sheet.innerHTML = `
    <div class="grab"></div>
    <div class="sheet-head">
      <h2>Feedback zu diesem Element</h2>
      <button type="button" class="sheet-close" data-close aria-label="Schließen">×</button>
    </div>
    <div class="target-card">
      <div class="target-thumb">${input.selection.tag}</div>
      <div class="target-meta">
        <div class="tag">${label}</div>
        <div class="quote">${quote.replace(/</g, '&lt;')}</div>
        <div style="margin-top:4px">${input.selection.selector}</div>
      </div>
    </div>
    <p class="hint">Website-Feedback — bitte keine Gesundheitsdaten.</p>
    <div class="voice-chip" data-voice-chip>
      <span data-voice-text>🎙 Sprachnachricht</span>
      <button type="button" class="x" data-voice-clear title="Aufnahme verwerfen" aria-label="Aufnahme verwerfen">×</button>
    </div>
    <div class="composer" data-composer>
      <textarea rows="1" placeholder="Nachricht schreiben…" data-msg></textarea>
      <div class="composer-actions">
        <div class="mic-wrap" data-mic-wrap>
          <div class="mic-split" data-mic-split>
            <button type="button" class="mic-main" data-rec title="Sprachnachricht" aria-label="Sprachnachricht aufnehmen">${MIC_SVG}</button>
            <button type="button" class="mic-chev" data-mic-menu title="Mikrofon wählen" aria-label="Mikrofon wählen">▾</button>
          </div>
          <div class="mic-menu" data-mic-list role="menu"></div>
        </div>
        <button type="button" class="icon-btn send" data-send hidden disabled title="Absenden" aria-label="Absenden">${SEND_SVG}</button>
      </div>
    </div>
    <p class="status" data-status></p>
  `
  shadow.appendChild(sheet)

  const textarea = sheet.querySelector('[data-msg]') as HTMLTextAreaElement
  const composer = sheet.querySelector('[data-composer]') as HTMLElement
  const micWrap = sheet.querySelector('[data-mic-wrap]') as HTMLElement
  const micSplit = sheet.querySelector('[data-mic-split]') as HTMLElement
  const micBtn = sheet.querySelector('[data-rec]') as HTMLButtonElement
  const micMenuBtn = sheet.querySelector('[data-mic-menu]') as HTMLButtonElement
  const micList = sheet.querySelector('[data-mic-list]') as HTMLElement
  const sendBtn = sheet.querySelector('[data-send]') as HTMLButtonElement
  const closeBtn = sheet.querySelector('[data-close]') as HTMLButtonElement
  const status = sheet.querySelector('[data-status]') as HTMLElement
  const voiceChipEl = sheet.querySelector('[data-voice-chip]') as HTMLElement
  const voiceChipText = sheet.querySelector('[data-voice-text]') as HTMLElement
  const voiceClear = sheet.querySelector('[data-voice-clear]') as HTMLButtonElement

  const recorder = createAudioRecorder()
  let audio: AudioCapture | null = null
  let preferredDeviceId: string | undefined

  const syncComposer = () => {
    const hasText = !!textarea.value.trim()
    const canSend = hasText || !!audio
    if (hasText || audio) {
      micWrap.hidden = true
      sendBtn.hidden = false
      sendBtn.disabled = !canSend
    } else {
      micWrap.hidden = false
      sendBtn.hidden = true
      sendBtn.disabled = true
    }
  }

  const autoGrow = () => {
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(160, Math.max(40, textarea.scrollHeight))}px`
  }

  const close = () => {
    if (recorder.isRecording()) void recorder.stop()
    root.remove()
    input.onDone?.()
  }

  backdrop.addEventListener('click', close)
  closeBtn.addEventListener('click', close)
  textarea.addEventListener('input', () => {
    syncComposer()
    autoGrow()
  })

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close()
  }
  window.addEventListener('keydown', onKey)

  voiceClear.addEventListener('click', () => {
    audio = null
    voiceChipEl.classList.remove('show')
    composer.classList.remove('ready')
    syncComposer()
  })

  const fillMicDevices = async () => {
    micList.innerHTML = ''
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const inputs = devices.filter((d) => d.kind === 'audioinput')
      if (!inputs.length) {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.textContent = 'Standard-Mikrofon'
        btn.className = 'active'
        micList.appendChild(btn)
        return
      }
      inputs.forEach((d, i) => {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.textContent = d.label || `Mikrofon ${i + 1}`
        if ((preferredDeviceId || '') === (d.deviceId || '') || (!preferredDeviceId && i === 0)) {
          btn.className = 'active'
        }
        btn.addEventListener('click', () => {
          preferredDeviceId = d.deviceId
          micList.querySelectorAll('button').forEach((b) => b.classList.remove('active'))
          btn.classList.add('active')
          micList.classList.remove('open')
        })
        micList.appendChild(btn)
      })
    } catch {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.textContent = 'Standard-Mikrofon'
      btn.className = 'active'
      micList.appendChild(btn)
    }
  }

  micMenuBtn.addEventListener('click', async (e) => {
    e.stopPropagation()
    await fillMicDevices()
    micList.classList.toggle('open')
  })

  document.addEventListener(
    'pointerdown',
    (e) => {
      if (!micList.classList.contains('open')) return
      const path = e.composedPath()
      if (path.includes(micWrap)) return
      micList.classList.remove('open')
    },
    true,
  )

  micBtn.addEventListener('click', async () => {
    status.classList.remove('error')
    try {
      if (recorder.isRecording()) {
        audio = await recorder.stop()
        micBtn.innerHTML = MIC_SVG
        micSplit.classList.remove('recording')
        composer.classList.remove('recording')
        if (audio) {
          voiceChipText.textContent = `🎙 Sprachnachricht · ${formatDuration(audio.durationMs)}`
          voiceChipEl.classList.add('show')
          composer.classList.add('ready')
          status.textContent = ''
        } else {
          voiceChipEl.classList.remove('show')
          status.textContent = ''
        }
        syncComposer()
        return
      }
      audio = null
      voiceChipEl.classList.remove('show')
      composer.classList.remove('ready')
      await recorder.start(preferredDeviceId)
      micBtn.innerHTML = STOP_SVG
      micSplit.classList.add('recording')
      composer.classList.add('recording')
      status.textContent = 'Aufnahme läuft…'
      micList.classList.remove('open')
      syncComposer()
    } catch (err) {
      status.classList.add('error')
      status.textContent = err instanceof Error ? err.message : 'Mikrofon nicht verfügbar'
      micBtn.innerHTML = MIC_SVG
      micSplit.classList.remove('recording')
      composer.classList.remove('recording')
    }
  })

  sendBtn.addEventListener('click', async () => {
    if (sendBtn.disabled) return
    sendBtn.disabled = true
    status.classList.remove('error')
    status.textContent = 'Sende…'
    try {
      const result = await submitFeedback({
        apiBase: input.apiBase,
        token: input.token,
        selection: input.selection,
        text: textarea.value,
        audio,
      })
      const preview = textarea.value.trim() || (audio ? `Sprachnachricht · ${formatDuration(audio.durationMs)}` : quote)
      input.onSent?.(result, preview)
      status.textContent = `Gesendet: ${result.todoKey}`
      window.removeEventListener('keydown', onKey)
      window.setTimeout(close, 700)
    } catch (err) {
      status.classList.add('error')
      status.textContent = err instanceof Error ? err.message : 'Senden fehlgeschlagen'
      syncComposer()
    }
  })

  syncComposer()
  autoGrow()
  window.setTimeout(() => textarea.focus(), 50)
}
